import React, { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { APPRoutes } from "../../Utils/Route";
import "../../Styles/ValidationExpires.css";

const LinkExpired = () => {
  const canvasRef = useRef(null);
  const rootRef = useRef(null);
  const parallaxRef = useRef(null);
  const hourglassRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");

    const resize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    resize();
    window.addEventListener("resize", resize);

    // Warm golden yellow sand tones matching the hourglass sand color (#f5b942)
    const SAND_COLORS = ["#f5b942", "#f7c85c", "#eab308", "#f5b027", "#fde047", "#f59e0b"];

    class Particle {
      constructor() { this.reset(); }
      reset() {
        const rect = hourglassRef.current ? hourglassRef.current.getBoundingClientRect() : null;
        const cx = rect ? rect.left + rect.width / 2 : canvas.width / 2;
        const cy = rect ? rect.top + rect.height * 0.72 : canvas.height * 0.32;
        this.x = cx + (Math.random() - 0.5) * 8;
        this.y = cy + Math.random() * 10;
        this.size = Math.random() * 2.2 + 0.6;
        this.speedY = Math.random() * 1.3 + 0.5;
        this.speedX = (Math.random() - 0.5) * 0.35;
        this.color = SAND_COLORS[Math.floor(Math.random() * SAND_COLORS.length)];
        this.opacity = Math.random() * 0.75 + 0.25;
        this.life = 0;
        this.maxLife = Math.random() * 120 + 60;
      }
      update() {
        this.y += this.speedY;
        this.x += this.speedX;
        this.life++;
        if (this.life > this.maxLife) this.reset();
      }
      draw() {
        ctx.save();
        ctx.globalAlpha = this.opacity * (1 - this.life / this.maxLife);
        ctx.fillStyle = this.color;
        ctx.beginPath();
        ctx.arc(this.x, this.y, this.size, 0, Math.PI * 2);
        ctx.fill();
        ctx.restore();
      }
    }

    const particles = [];
    for (let i = 0; i < 90; i++) {
      const p = new Particle();
      p.life = Math.random() * p.maxLife;
      particles.push(p);
    }

    let rafId;
    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      particles.forEach((p) => { p.update(); p.draw(); });
      rafId = requestAnimationFrame(animate);
    };
    animate();

    return () => {
      window.removeEventListener("resize", resize);
      cancelAnimationFrame(rafId);
    };
  }, []);

  useEffect(() => {
    const root = rootRef.current;
    const el = parallaxRef.current;
    if (!root || !el) return undefined;

    const handleMove = (e) => {
      const { innerWidth, innerHeight } = window;
      const dx = (e.clientX / innerWidth - 0.5) * 2;
      const dy = (e.clientY / innerHeight - 0.5) * 2;
      el.style.transform = `perspective(700px) rotateY(${dx * 8}deg) rotateX(${-dy * 8}deg)`;
    };
    const handleLeave = () => {
      el.style.transform = "perspective(700px) rotateY(0deg) rotateX(0deg)";
    };

    root.addEventListener("mousemove", handleMove);
    root.addEventListener("mouseleave", handleLeave);
    return () => {
      root.removeEventListener("mousemove", handleMove);
      root.removeEventListener("mouseleave", handleLeave);
    };
  }, []);

  const handleResend = () => {
    navigate(APPRoutes.FORGOT_PASSWORD);
  };

  const handleBack = () => {
    navigate(APPRoutes.LOGIN);
  };

  return (
    <div className="le-root" ref={rootRef}>
      {/* Guide lines */}
      <div className="le-lines">
        <div className="le-line" />
        <div className="le-line" />
        <div className="le-line" />
      </div>

      {/* Sand canvas */}
      <canvas ref={canvasRef} className="le-canvas" />

      <div className="le-container">

        {/* Hourglass */}
        <div className="le-icon-parallax" ref={parallaxRef}>
          <div className="le-hourglass" ref={hourglassRef}>
            <svg width="100" height="93" viewBox="0 0 80 100" fill="none">
              {/* Top glass */}
              <path d="M10 5 L70 5 L50 48 L30 48 Z" fill="rgba(var(--hc-gradient-mid-rgb), 0.08)" stroke="rgba(var(--hc-gradient-mid-rgb), 0.6)" strokeWidth="1.5" strokeLinejoin="round" />
              {/* Bottom glass */}
              <path d="M30 52 L50 52 L70 95 L10 95 Z" fill="rgba(var(--hc-gradient-mid-rgb), 0.08)" stroke="rgba(var(--hc-gradient-mid-rgb), 0.6)" strokeWidth="1.5" strokeLinejoin="round" />
              {/* Frames */}
              <rect x="8" y="2" width="64" height="5" rx="2" fill="rgba(var(--hc-gradient-start-rgb), 0.55)" stroke="rgba(var(--hc-gradient-start-rgb), 0.7)" strokeWidth="1" />
              <rect x="8" y="93" width="64" height="5" rx="2" fill="rgba(var(--hc-gradient-start-rgb), 0.55)" stroke="rgba(var(--hc-gradient-start-rgb), 0.7)" strokeWidth="1" />
              {/* Sand top draining */}
              <g className="le-sand-top">
                <path d="M14 10 L66 10 L50 44 L30 44 Z" fill="#f5b942" />
              </g>
              {/* Sand bottom pile */}
              <path d="M22 88 Q40 72 58 88 Z" fill="#f5b942" />
              {/* Crack */}
              <path className="le-crack" d="M38 30 L35 38 L40 43" stroke="rgba(var(--hc-red-500-rgb), 0.85)" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
              {/* Drip */}
              <line x1="40" y1="48" x2="40" y2="52" stroke="#f5b942" strokeWidth="1.5" strokeLinecap="round">
                <animate attributeName="opacity" values="1;0.1;1" dur="0.6s" repeatCount="indefinite" />
              </line>
            </svg>
          </div>
        </div>

        <div className="le-divider" />

        {/* Badge */}
        <div className="le-badge">
          <span className="le-badge-dot" />
          Link Expired
        </div>

        {/* Heading */}
        <h1 className="le-h1">
          Your reset link has<br />
          <em>slipped away</em>
        </h1>

        {/* Description */}
        <p className="le-p">
          Password reset links are only valid for{" "}
          <strong>24 hours</strong> for your security. This one has already
          expired and can no longer be used.
        </p>

        {/* Status */}
        <div className="le-status">Expired · No longer valid</div>
        <div className="le-actions">
          <button className="le-btn-primary" onClick={handleResend}>
            <span>Request a new link</span>
          </button>
          <button className="le-btn-secondary" onClick={handleBack}>
            Back to Sign in
          </button>
        </div>
      </div>
    </div>
  );
};

export default LinkExpired;